import {
    call, put, select, takeEvery, takeLatest, throttle
} from 'redux-saga/effects';
import {
    clearCapiSession, createCapiSession, SESSION_CREATION_ERROR, SET_CAPI_SESSION
} from 'capi/redux';
import { getCapiSessionID } from 'capi/redux/immutable';
import { v4 as uuid } from 'uuid';

import { LOCATION_CHANGE } from '../routing/routingModule';

import {
    getBuyFlow, getHostname, getPath, getReferrer, getUrlParams
} from './utils';
import {
    capiSessionCallIndicator,
    clearStorefrontSession,
    GET_OR_CREATE_STOREFRONT_SESSION,
    logSessionInfoInSegment,
    setCustomerId,
    setStorefrontSession
} from './sessionModule';
import { getCapiSessionCallIndicator, getCustomerId, getStorefrontSessionKey } from './sessionSelectors';
import { logError } from '../utils/errorUtils';
import { getStepConfigs } from '../routing/flowSelectors';
import { getProductDetailsPagePath, SYNC_FLOW_PATHS } from '../routing/flow';
import { getPreferredValues, hasProductDetailsPageEnabled } from '../app/bootstrap';
import { removeQueryParams } from '../routing/routing';



const getCreateCapiSessionAction = (sessionKey, pagePathName) => createCapiSession({
    sessionKey,
    host: getHostname(),
    refUrl: getReferrer(),
    buyFlow: getBuyFlow(),
    pagePath: pagePathName,
    ...getUrlParams()
});


function* pagePath() {
    const landedPath = getPath().toLowerCase().replace(/\/+$/, '');
    const stepConfigs = yield select(getStepConfigs);
    const keysArray = stepConfigs.keySeq().toArray();

    const asyncPaths = keysArray.reduce((acc, key) => {
        const storeKey = `/store/${key}`;
        
        if (key === 'self_service/v2/about/:docType') {
            acc.push(
                '/store/self_service/v2/about/faq',
                '/store/self_service/v2/about/privacy_policy',
                '/store/self_service/v2/about/terms_and_conditions'
            );
        } else {
            
            acc.push(storeKey);
        }
        return acc;
    }, []);

    
    const foundSyncPath = SYNC_FLOW_PATHS.find(syncPath => syncPath === landedPath);

    if (foundSyncPath === '') {
        return ('/');
    }
    if (foundSyncPath) {
        if (!hasProductDetailsPageEnabled() && foundSyncPath === getProductDetailsPagePath()) {
            return '/store/program';
        }
        return foundSyncPath; 
    }

    
    const foundasyncPath = asyncPaths.find(updatedKey => updatedKey === landedPath);
    if (foundasyncPath) {
        return foundasyncPath; 
    }
    return 'PAGE_NOT_FOUND'; 
}


function* getOrCreateSession({ location = null }) {
    const callInProgress = yield select(getCapiSessionCallIndicator);
    if (!callInProgress) {
        const sessionId = yield select(getCapiSessionID);
        if (!sessionId) {
            yield call(getOrCreateSessionKey);
            yield call(getOrCreateCustomerId);
            const sessionKey = yield select(getStorefrontSessionKey);
            yield put(capiSessionCallIndicator());
            yield put(logSessionInfoInSegment('Session API called'));
            const pagePathName = yield call(pagePath);
            yield put(getCreateCapiSessionAction(sessionKey, pagePathName));
            if (location && window.RISKX) {
                
                window.RISKX.setSid(sessionKey);
                window.RISKX.go(location.location.pathname);
            }
        }
    }
}


function* getOrCreateSessionKey() {
    let sessionKey = yield select(getStorefrontSessionKey);
    if (!sessionKey) {
        sessionKey = yield uuid();
        yield put(setStorefrontSession(sessionKey));
    }
}


function* getOrCreateCustomerId() {
    let customerId = yield select(getCustomerId);
    if (!customerId) {
        const preferredValues = yield select(getPreferredValues);
        customerId = preferredValues.cid;
        yield put(setCustomerId(customerId));
        
        yield call(removeQueryParams, ['cid']);
    }
}


function* innerClearSessionSaga() {
    yield put(logSessionInfoInSegment('Clearing storefront and capi session'));
    yield put(clearStorefrontSession());
    yield put(clearCapiSession());
}



export const createClearSessionSaga = (action) => {
    function* clearSessionSaga() {
        yield takeEvery(action, innerClearSessionSaga);
    }
    return clearSessionSaga;
};


export function* getOrCreateSessionSaga() {
    yield takeLatest(GET_OR_CREATE_STOREFRONT_SESSION, getOrCreateSession);
}


export function* sessionApiFailureWatcherSaga() {
    yield takeEvery(SESSION_CREATION_ERROR, function* sessionApiFailureSaga(action) {
        const { error } = action;
        logError(`Session Api failed with: ${error}`);
        yield put(capiSessionCallIndicator(false));
        yield put(logSessionInfoInSegment('Session Api failed with error', { error }));
    });
}


export function* sessionApiSuccessWatcherSaga() {
    yield takeEvery(SET_CAPI_SESSION, function* sessionApiSuccessSaga() {
        yield put(capiSessionCallIndicator(false));
        yield put(logSessionInfoInSegment('Session Api call successful'));
    });
}


export function* checkSessionSaga() {
    yield throttle(1000, LOCATION_CHANGE, getOrCreateSession);
}
